import type { Ring, Vec2 } from '../../../../geo/types';
import type { ChapelPlan } from '../churchModel';
import { buildChapel, buildHallAisle, SIDE_PARTS } from '../churchSideParts';
import { buttress, centredWindow } from '../masonry';
import { fittedButtress } from './oudeKerkButtress';
import { buildLichtbeuk } from './oudeKerkLichtbeuk';
import type { OudeKerkBuild } from './oudeKerkParts';
import { OUDE_KERK } from './oudeKerkPlan';

/**
 * The aisles of the hall of c.1390 under their own roofs as far as the crossing, where the arms of
 * St George and St Sebastian cross them at chapel height (Janse 2004, section 1.11); the chapels
 * between the aisles' buttresses; and the middle vessel raised in 1510-12. Pinnacled buttresses stand
 * between the chapel gables as the painting shows them (scan 2700-3180 x 2540-2940), as deep as the
 * trace leaves room for; at the arms' corners as wide and deep as traced.
 */
const BUTTRESS = { width: 0.8, depth: 1.0, rise: 0.6, pinnacle: 2.6 } as const;

function buildAisles(s: OudeKerkBuild): void {
  for (const a of s.plan.aisles) {
    const [inner, outer] = a.side > 0 ? [a.v0, a.v1] : [a.v1, a.v0];
    const run = { side: a.side, u0: a.u0, u1: s.plan.crossing.u0, inner, outer, eaveY: a.eaveY, topY: a.ridgeY, roof: 'gable' as const };
    buildHallAisle(s.builder, s.stone, s.frame, run);
  }
}

/** A buttress at every boundary of a row of chapels, on its outer line, crowned with a pinnacle; those at the row's ends stand inside its corners. */
function rowButtresses(s: OudeKerkBuild, row: readonly ChapelPlan[]): void {
  const bounds = [...new Set(row.flatMap((c) => [c.u0, c.u1]))];
  const [first, last] = [Math.min(...bounds), Math.max(...bounds)];
  const half = BUTTRESS.width / 2;
  for (const u of bounds) {
    const c = row.find((r) => r.u0 === u || r.u1 === u)!;
    const at = u === first ? u + half : u === last ? u - half : u;
    const spec = { width: BUTTRESS.width, depth: BUTTRESS.depth, height: c.eaveY + BUTTRESS.rise, pinnacle: BUTTRESS.pinnacle };
    fittedButtress(s, [at, c.outer], [0, c.side], spec);
  }
}

/** The corner buttresses of the arms, as wide and deep as the trace draws them. */
function armButtresses(s: OudeKerkBuild): void {
  const stone = { ...s.stone, roof: s.stone.wall };
  const arms = OUDE_KERK.transeptChapels.arms;
  s.plan.transeptArms.forEach((arm, i) => {
    const traced = i === 0 ? arms.north : arms.south;
    for (const [u0, u1, reach] of traced.buttresses) {
      const at: Vec2 = [(u0 + u1) / 2, arm.outer];
      const spec = { width: u1 - u0, depth: Math.abs(reach - arm.outer), height: arm.eaveY + BUTTRESS.rise, pinnacle: BUTTRESS.pinnacle };
      buttress(s.builder, stone, s.frame, at, [0, arm.side], spec);
    }
  });
}

/* The south portal: a doorway from the ground in its chapel's front and the library's window over it. */
function portalFront(chapel: ChapelPlan): Ring[] {
  const { width, top, windowSill } = OUDE_KERK.portals.south;
  const front = chapel.u1 - chapel.u0;
  const windowTop = chapel.eaveY + SIDE_PARTS.chapelWindowReach * (chapel.ridgeY - chapel.eaveY);
  return [...centredWindow(front, 0, top, width / front), ...centredWindow(front, windowSill, windowTop, SIDE_PARTS.chapelWindowShare)];
}

function buildChapels(s: OudeKerkBuild): void {
  const { naveChapels, transeptChapels, transeptArms } = s.plan;
  const arm = transeptArms.find((a) => a.side < 0);
  const portal = arm && transeptChapels.find((c) => c.side < 0 && Math.abs(c.u1 - arm.u0) < 1e-6);
  for (const chapel of [...naveChapels, ...transeptChapels, ...transeptArms]) {
    buildChapel(s.builder, s.stone, s.frame, chapel, chapel === portal ? portalFront(chapel) : undefined);
  }
  for (const side of [1, -1] as const) {
    rowButtresses(s, naveChapels.filter((c) => c.side === side));
    rowButtresses(s, transeptChapels.filter((c) => c.side === side));
  }
  armButtresses(s);
}

export function buildOudeKerkNave(s: OudeKerkBuild): void {
  buildLichtbeuk(s);
  buildAisles(s);
  buildChapels(s);
}
